/**
 * Agent events -> a flat turn list, for the embedded chats.
 *
 * The kernel editor's chat renders a short transcript in a narrow column and
 * wants none of ChatConsole's machinery: no minimap, no persistence engine, no
 * slash commands. What it needs is the fold from the agent's event stream onto
 * a list a component can map over. (The trace dock's chat draws with
 * ChatConsole's own transcript now, from ChatConsole's applyAgentEvent.)
 *
 * Each top-level item is either a user message or an agent turn. Agent turns
 * accumulate text and tool calls as the events stream in.
 */

/**
 * Fold one agent event into the turn list. Pure, so a chat can replay a
 * stored event log and land on the same transcript.
 */
export function reduceChatEvent(items, event) {
  switch (event.type) {
    case "turn_start":
      return [...items, { kind: "agent", id: `turn:${items.length}`, text: "", tools: [], status: "running" }];
    case "turn_end": {
      // Why the model call stopped: "length" means its words were cut off.
      const stopReason = event.message?.stopReason;
      return updateLastAgentTurn(items, (t) => ({ ...t, status: "done", ...(stopReason ? { stopReason } : {}) }));
    }
    case "agent_end": {
      // Belt-and-suspenders cleanup. The agent loop can finish without a
      // final turn_end (errors, aborts, providers that don't emit symmetric
      // events). Force every still-"running" turn to "done" so the
      // empty-turn filter can drop them, and surface a fatal `errorMessage`
      // from the run as a visible note.
      const last = event.messages?.[event.messages.length - 1];
      let next = items.map((it) => (it.kind === "agent" && it.status === "running"
        ? { ...it, status: "done" }
        : it));
      if (last?.errorMessage) {
        next = updateLastAgentTurn(next, (t) => ({
          ...t,
          error: last.errorMessage,
          status: "done",
        }));
      }
      return next;
    }
    case "message_update": {
      const inner = event.assistantMessageEvent;
      if (!inner) return items;
      // The model call restarted after the provider went quiet (piStreamFn.js):
      // the retry rewrites the message from the top.
      if (inner.restart) items = updateLastAgentTurn(items, (t) => ({ ...t, text: "", tools: [] }));
      if (inner.type === "text_delta" && inner.delta) {
        return updateLastAgentTurn(items, (t) => ({ ...t, text: (t.text || "") + inner.delta }));
      }
      if (inner.type === "toolcall_start") {
        return updateLastAgentTurn(items, (t) => ({
          ...t,
          tools: [...t.tools, {
            contentIndex: inner.contentIndex,
            name: "tool",
            args: "",
            status: "preparing",
            result: null,
          }],
        }));
      }
      if (inner.type === "toolcall_delta") {
        return updateLastAgentTurn(items, (t) => ({
          ...t,
          tools: t.tools.map((tc) => (tc.contentIndex === inner.contentIndex
            ? { ...tc, args: (tc.args || "") + (inner.delta || "") }
            : tc)),
        }));
      }
      if (inner.type === "toolcall_end") {
        const callId = inner.toolCall?.id;
        return updateLastAgentTurn(items, (t) => ({
          ...t,
          tools: t.tools.map((tc) => (tc.contentIndex === inner.contentIndex ? {
            ...tc,
            id: callId,
            name: inner.toolCall?.name || tc.name,
            args: inner.toolCall?.arguments ?? tc.args,
            status: "queued",
          } : tc)),
        }));
      }
      return items;
    }
    case "tool_execution_start":
      return updateLastAgentTurn(items, (t) => ({
        ...t,
        tools: t.tools.map((tc) => (tc.id === event.toolCallId
          ? { ...tc, status: "running", name: event.toolName || tc.name }
          : tc)),
      }));
    case "tool_execution_end":
      return updateLastAgentTurn(items, (t) => ({
        ...t,
        tools: t.tools.map((tc) => (tc.id === event.toolCallId ? {
          ...tc,
          status: event.isError ? "error" : "done",
          result: event.result?.content?.[0]?.text || (event.isError ? "error" : "done"),
          // The structured half of the result, which a row can describe in the
          // reader's words; the text is written for the model.
          details: event.result?.details ?? null,
          // This call asked to end the run (the loop breaker, suggest_kernel).
          ...(event.result?.terminate === true ? { terminated: true } : {}),
        } : tc)),
      }));
    default:
      return items;
  }
}

export function updateLastAgentTurn(items, fn) {
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].kind === "agent") {
      const next = items.slice();
      next[i] = fn(items[i]);
      return next;
    }
  }
  return items;
}

/**
 * Turns worth drawing. The loop opens a turn node it sometimes never fills -
 * a wrap-up turn after the last tool call where the model has nothing more to
 * say and just emits an empty turn_end. Running turns stay (still mid-flight)
 * and so do error turns (they have visible content).
 */
export function visibleChatTurns(items) {
  return items.filter((it) => it.kind === "user"
    || it.status === "running"
    || (it.text && it.text.trim())
    || (it.tools && it.tools.length)
    || it.error);
}

/**
 * Force every open turn closed. A chat calls this when its own prompt call
 * settles, so a run that ended without symmetric events cannot leave a
 * dangling AGENT header on screen.
 */
export function settleOpenTurns(items) {
  return items.map((it) => (it.kind === "agent" && it.status === "running"
    ? { ...it, status: "done" }
    : it));
}
